import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { Button } from '@components/Core/Button';
import { Input } from '@inputs/Input';
import { ProfileFormTransFields } from '@forms/ProfileForm/types';
import { profileTranslations } from '@pages/Profile/translations';
import {
  DeleteAccountPopupTransFields,
  DeleteAccountStepType,
  IDeleteAccountPopupForm,
} from './types';
import { deleteAccountPopupTranslations } from './translations';
import { ChangeProfileNamePopupTransFields } from '../ChangeProfileNamePopup/types';
import { changeProfileNamePopupTranslations } from '../ChangeProfileNamePopup/translations';

export const DeleteAccountPopup: FC = () => {
  const {
    popupStore: { closePopup },
    profileStore: { deleteProfile },
    authStore: { logout },
  } = useStore();

  const [step, setStep] = useState<DeleteAccountStepType>('confirm');

  const useFormMethods = useForm<IDeleteAccountPopupForm>();

  const { handleSubmit, watch } = useFormMethods;

  const { t } = useMyTranslations<
    | DeleteAccountPopupTransFields
    | Extract<ProfileFormTransFields, 'Delete account'>
    | Extract<ChangeProfileNamePopupTransFields, 'Cancel'>
  >([
    deleteAccountPopupTranslations.name,
    profileTranslations.name,
    changeProfileNamePopupTranslations.name,
  ]);

  const navigate = useNavigate();

  useEffect(() => {
    setStep('confirm');
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: deleteProfile,
    mutationKey: ['deleteProfile'],
    onSuccess: () => {
      logout();
      navigate(`/login`, { replace: true });
    },
  });

  const onSubmit = useCallback(() => {
    mutate();
  }, []);

  if (step === 'confirm')
    return (
      <div className="flex flex-col justify-stretch items-center gap-y-[16px]">
        <p className="default-text mb-[8px] text-error">
          {t('Delete account', { ns: profileTranslations.name })}
        </p>
        <p className="default-text text-error mb-[8px] text-center">{t('ConfirmText')}</p>
        <Button text={t('No')} onClick={closePopup} />
        <Button text={t('Yes')} onClick={() => setStep('delete')} styleType="delete" />
      </div>
    );

  return (
    <div className="flex flex-col justify-stretch items-center gap-y-[16px]">
      <p className="default-text mb-[8px] text-error text-center">{t('ConfirmTitle')}</p>
      <div className="default-text  mb-[8px] text-center">
        <span>{t('Enter the word')}</span>
        <span className="text-error"> delete </span>
        <span>{t('to confirm')}</span>
      </div>
      <FormProvider {...useFormMethods}>
        <Input name="confirm" styleProp="mb-[16px] h-[40px]" />
      </FormProvider>
      <Button
        text={t('Delete account', { ns: profileTranslations.name })}
        onClick={handleSubmit(onSubmit)}
        disabled={watch('confirm') !== 'delete' || isPending}
        styleType="delete"
      />
      <Button
        text={t('Cancel', { ns: changeProfileNamePopupTranslations.name })}
        onClick={closePopup}
      />
    </div>
  );
};
